// 周日期字符串 -> 年/月元数据解析与分组工具
// 约定：dateRanges 元素为 "YYYY-MM-DD - YYYY-MM-DD"（周起始日 - 周结束日）
// 分组一律以「周起始日」为准，保证与父组件传入的 dateRanges 顺序一致。

export const JUMP_TO_LATEST_WEEK = true; // true: 选年/月跳到该时段最新周；false: 跳到首周

// 特殊哨兵值：表示「全部时间」标签。它不属于 dateRanges 周列表，
// 由 NavBar 在周 tab 之后单独渲染为一个独立 tab（仅在搜索/标签页开启）。
// 之所以用 'all'：周字符串严格为 "YYYY-MM-DD - YYYY-MM-DD"，不可能与之碰撞，且 URL 友好。
export const ALL_TIME_RANGE = 'all';

// "2024-07-15 - 2024-07-21" -> { year:2024, month:7, ym:'2024-07' }
export function parseWeekMeta(range) {
  const start = String(range).split(' - ')[0]; // "2024-07-15"
  const [y, m] = start.split('-').map(Number);
  return {
    year: y,
    month: m,
    ym: `${y}-${String(m).padStart(2, '0')}`,
  };
}

// 由 dateRanges 得到去重升序年份，如 [2024, 2025, 2026]
export function collectYears(dateRanges) {
  if (!Array.isArray(dateRanges)) return [];
  const set = new Set();
  dateRanges.forEach((r) => {
    const meta = parseWeekMeta(r);
    if (meta.year) set.add(meta.year);
  });
  return Array.from(set).sort((a, b) => a - b);
}

// 由 dateRanges + year 得到该年有数据的月份（去重升序），如 2024 -> [7,8,...,12]
export function collectMonths(dateRanges, year) {
  if (!Array.isArray(dateRanges) || !year) return [];
  const set = new Set();
  dateRanges.forEach((r) => {
    const meta = parseWeekMeta(r);
    if (meta.year === year) set.add(meta.month);
  });
  return Array.from(set).sort((a, b) => a - b);
}

// 取某时段（年 或 年月）内最新（或首）周字符串
export function pickWeekInPeriod(dateRanges, { year, ym, latest = true } = {}) {
  if (!Array.isArray(dateRanges) || (!year && !ym)) return null;
  let candidates = dateRanges;
  if (ym) {
    candidates = candidates.filter((r) => parseWeekMeta(r).ym === ym);
  } else if (year) {
    candidates = candidates.filter((r) => parseWeekMeta(r).year === year);
  }
  if (!candidates.length) return null;
  // 父组件传入的 dateRanges 已按起始日升序；此处再防御性排序一次
  const sorted = [...candidates].sort(
    (a, b) => new Date(parseWeekMeta(a).year, parseWeekMeta(a).month) - new Date(parseWeekMeta(b).year, parseWeekMeta(b).month)
  );
  return latest ? sorted[sorted.length - 1] : sorted[0];
}

// 从 featured_at（ISO 串如 "2026-08-17T07:01:00Z"）提取日期部分 "YYYY-MM-DD"
// 用于「日筛选」取条目发布日期，时区无关（仅取日期前缀）。
export function parseFeaturedAt(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

// 由当前周 products 数组提取 featured_at 去重升序 ymd 数组
//   返回: ["2026-08-17","2026-08-18",...] 或 []（无 featured_at 数据）
// NavBar 的「日期」面板 chips 直接消费此结果；早期无 featured_at 的周返回 [] -> 按钮 disabled。
export function collectDaysInProducts(products) {
  if (!Array.isArray(products)) return [];
  const set = new Set();
  products.forEach((p) => {
    const ymd = parseFeaturedAt(p && p.featured_at);
    if (ymd) set.add(ymd);
  });
  return Array.from(set).sort();
}

// 统计当前周 products 中每个 featured_at 日期对应的条目数量
//   返回: { "2026-08-17": 3, "2026-08-18": 2, ... } 或 {}（无 featured_at 数据）
// 用于 NavBar 日 chip 后展示 (N) 数量，与 collectDaysInProducts 共用同一数据。
export function countProductsByDay(products) {
  if (!Array.isArray(products)) return {};
  const counts = {};
  products.forEach((p) => {
    const ymd = parseFeaturedAt(p && p.featured_at);
    if (ymd) {
      counts[ymd] = (counts[ymd] || 0) + 1;
    }
  });
  return counts;
}
